import React, { useState, useEffect, useRef } from 'react';
import ReportDownloader from './components/ReportDownloader';
import BattenburgLoader from '../common/BattenburgLoader';
import DraggableToolbar from '../common/DraggableToolbar';
import {
  useLoadingArr, useSelectedDay, useSelectedMonth, useSelectedMonthVacancy,
  useHeromaId, usePasstiderId, useStation, useIsAuthorized,
  useSelectedPasstiderFile, useSelectedHeromaFile, useUserInfo,
  useHtmlReportDone, useMonthReportDone,
  useHasPasstider, useHasHeroma, useStationAntalEnheter, useStationAntalPersoner,
  useMonthlyReportNames, useDailyReportNames, useImpersonatedUserId
} from '../contexts/GlobalStateContext';
import { fetchWithAuth, getUserInfoById } from './../services/api';
import { getTodaysDate, getSchemaDates } from '../services/UtilityServices';
import { toast } from 'react-toastify';

const Home = () => {
  const { impersonatedUserId } = useImpersonatedUserId();
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const { monthlyReportNames, setMonthlyReportNames } = useMonthlyReportNames()
  const { dailyReportNames, setDailyReportNames } = useDailyReportNames()
  const { selectedDay, setSelectedDay } = useSelectedDay()
  const { selectedMonth, setSelectedMonth } = useSelectedMonth()
  const { selectedMonthVacancy, setSelectedMonthVacancy } = useSelectedMonthVacancy()

  const loadingKeys = ['btn1', 'btn2', 'btn3', 'btn4', 'btn5', 'btn6', 'default'];
  const { loadingArr, setLoadingArr } = useLoadingArr();
  const [stationer, setStationer] = useState([]);
  const { isAuthorized } = useIsAuthorized();

  const { station, setStation } = useStation();
  const { userInfo } = useUserInfo();
  const { stationAntalEnheter, setStationAntalEnheter } = useStationAntalEnheter();
  const { stationAntalPersoner, setStationAntalPersoner } = useStationAntalPersoner();

  const { hasPasstider, setHasPasstider } = useHasPasstider()
  const { hasHeroma, setHasHeroma } = useHasHeroma()
  const downloaderRef = useRef();

  const { htmlReportDone, setHtmlReportDone } = useHtmlReportDone();
  const { monthReportDone, setMonthReportDone } = useMonthReportDone();

  const [documents, setDocuments] = useState({ personal: [""], passtider: [""], heroma: [""] });

  const { passtiderId, setPasstiderId } = usePasstiderId();
  const { selectedPasstiderFile, setSelectedPasstiderFile } = useSelectedPasstiderFile();

  const { heromaId, setHeromaId } = useHeromaId();
  const { selectedHeromaFile, setSelectedHeromaFile } = useSelectedHeromaFile();
  const [passtiderHeromaNotMatching, setPasstiderHeromaNotMatching] = useState(false);
  const [sammanfattningItems, setSammanfattningItems] = useState([]);
  const [importMessages, setImportMessages] = useState([]);
  const [showSammanfattningModal, setShowSammanfattningModal] = useState(false);
  const [sammanfattningSortKey, setSammanfattningSortKey] = useState('datum');
  const [sammanfattningSortAsc, setSammanfattningSortAsc] = useState(true);

  const isSystemAdmin = userInfo?.role === 'SystemAdmin';

  const fetchDocuments = async (station) => {
    try {
      let url = `/api/documents/list?station=${station}`;

      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Kunde inte hämta filer');
      }
      const data = await response.json();
      setDocuments({
        personal: data.personal || [],
        passtider: data.passtider || [],
        heroma: data.heroma || [],
      });
    } catch (error) {
      const msg = error.message || '❌ Kunde inte hämta filer.';
      toast.error(msg);
    }
  }

  useEffect(() => {
    const loadData = async () => {
      if (isAuthorized) {
        const userId = impersonatedUserId || userInfo.userId
        if (!userId) return;
        try {
          await getStationer(userId);
          if (station !== '---') {
            await getDailyReportNames();
            await getMonthlyReportNames();
          }

          if (station && station !== '---') {
            await fetchDocuments(station);
            await checkIfPasstiderExists(station);
            await checkIfHeromaExists(station);
          }
          if (isSystemAdmin) {
            const data = await getUserInfoById(impersonatedUserId);
            setImpersonatedUser(data);
          }
        } catch (error) {
          console.error('Fel vid hämtning av användardata:', error);
        }
      } else {
        setDailyReportNames(['Underlag saknas']);
        setSelectedDay('');
        setMonthlyReportNames(['Underlag saknas']);
        setSelectedMonth('');
        setSelectedMonthVacancy('');
        setSelectedHeromaFile('');
        setSelectedPasstiderFile('');
        setHasHeroma(false);
        setHasPasstider(false);
        setPasstiderId('');
        setHeromaId('');
        setStationAntalEnheter('');
        setStationAntalPersoner('');
        setPasstiderHeromaNotMatching(false);
      }
    };

    loadData();
  }, [station, isAuthorized, impersonatedUserId, userInfo?.userId]);

  const anyLoading = () => loadingKeys.some(key => loadingArr[key]);

  const getAktuellaPersoner = async () => {
    try {
      let url = `/api/personal/person?station=${station}?allaStationer=false&aktuell=true`;

      const response = await fetchWithAuth(url);
      if (!response.ok) {
        throw new Error('Kunde inte hämta personer');
      }
      const data = await response.json();
      const personerUtanRoll = data.filter(p => p.rollId === 6);
      const namnLista = personerUtanRoll.slice(0, 10).map(p => p.namn);
      if (personerUtanRoll.length > 10) {
        namnLista.push('...');
      }
    } catch (error) {
      console.error(error);
    }
  }

  const clearReportNames = () => {
    setDailyReportNames(['Underlag saknas'])
    setSelectedDay('')
    setMonthlyReportNames(['Underlag saknas'])
    setSelectedMonthVacancy('')
  }

  const checkIfPasstiderExists = async (station) => {
    try {
      const response = await fetchWithAuth(`/api/passtider/passtider?station=${encodeURIComponent(station)}`);
      if (response.status === 200) {
        const data = await response.json();
        const exists = data === true;
        setHasPasstider(exists);
        return exists;
      }
      setHasPasstider(false);
      return false;
    } catch (error) {
      setHasPasstider(false);
      console.error("Fel vid kontroll av passtider:", error);
      return false;
    }
  }

  const checkIfHeromaExists = async (station) => {
    try {
      const response = await fetchWithAuth(`/api/heroma?station=${encodeURIComponent(station)}`);
      if (response.status === 200) {
        const data = await response.json();
        setHasHeroma(data === true);
      } else {
        setHasHeroma(false);
      }
    } catch (error) {
      setHasHeroma(false);
      console.error("Fel vid kontroll av heromaschema:", error);
    }
  }

  const getStationer = async (userId) => {
    try {
      if (!userId) return;
      const url = `/api/stationer/names?userId=${userId}`;
      const response = await fetchWithAuth(url);

      if (!response.ok) throw new Error('Kunde inte hämta stationer');
      const data = await response.json();
      setStationer(data.length === 0 ? [] : data);
      return data.length;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }

  const getDailyReportNames = async () => {
    try {
      const dates = await getSchemaDates(station);
      if (dates.length === 0) {
        setDailyReportNames(['Underlag saknas'])
        setSelectedDay('')
        return 0;
      }
      setDailyReportNames(dates);
      const today = getTodaysDate();
      setSelectedDay(dates.includes(today) ? today : dates[0]);
      return dates.length;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }

  const getMonthlyReportNames = async () => {
    try {
      const url = `/api/reports/names?type=manad&station=${encodeURIComponent(station)}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Kunde inte hämta månadsrapportnamn');
      const data = await response.json();
      if (data.length === 0) {
        setMonthlyReportNames(['Underlag saknas'])
        setSelectedMonth('')
        return 0;
      }
      setMonthlyReportNames(data);
      return data.length;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }

  const uploadHeromaSchema = async (key, userId) => {
    clearReportNames();
    if (!heromaId) {
      console.error("No file selected");
      return;
    }
    setImportMessages([]);
    setSammanfattningItems([]);
    try {
      setLoadingArr(prev => ({ ...prev, [key]: true }));
      let url = '/api/heroma';
      if (userId) url += `?userId=${userId}`;

      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        body: JSON.stringify({ documentId: heromaId, station: station })
      });

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg || 'Fel vid uppladdning av txt-fil.');
      }

      const data = await response.json();
      if (data.mergeResults) {
        const newName = documents.heroma.find(f => f.id === parseInt(heromaId))?.name || '';
        const prevName = selectedHeromaFile?.split('\n')[0];
        setSelectedHeromaFile(newName + '\n' + (prevName ? 'Föregående: ' + prevName : ''));
        await handleMerge(data);
        setStationAntalPersoner(data.stationAntal);
      } else if (data.length === 0) {
        if (station && station !== '---') await checkIfHeromaExists(station);
        setImportMessages([{ type: 'warning', text: 'Inga underlag skapade. Granska kombination passtider och heroma.' }]);
        setShowSammanfattningModal(true);
      }
    } catch (error) {
      setImportMessages([{ type: 'error', text: error.message || 'Fel vid uppladdning av txt-fil.' }]);
      setShowSammanfattningModal(true);
    } finally {
      setLoadingArr(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleMerge = async (importResult) => {
    const { stationAntal, mergeResults, totalReportsImported, autoMergedCount, sammanfattningItems: newItems } = importResult;

    setStationAntalPersoner(stationAntal);
    await getAktuellaPersoner();

    const dailyExists = await getDailyReportNames();
    const monthlyExists = await getMonthlyReportNames();

    if (station && station !== '---') await checkIfHeromaExists(station);

    if (dailyExists === 0 || monthlyExists === 0) {
      setImportMessages([{ type: 'warning', text: 'Inga underlag skapade. Granska kombination passtider och heroma.' }]);
      setSammanfattningItems([]);
      setShowSammanfattningModal(true);
      setHasHeroma(false);
      return;
    }

    setPasstiderHeromaNotMatching(false);

    const mergeMessages = mergeResults
      .filter(r => r.message && r.message.trim() !== '')
      .map(r => {
        const m = r.message.match(/(\d+)/);
        return m ? `${r.datum}: ${m[1]} merge` : `${r.datum}: ${r.message}`;
      })
      .join(', ');

    let successText;
    if (mergeMessages) {
      successText = `✅ Import klar! ${totalReportsImported} persondagar inlästa. ${mergeMessages}`;
    } else {
      successText = `✅ Import klar! ${totalReportsImported} persondagar inlästa`;
    }

    setImportMessages([{ type: 'success', text: successText }]);
    setSammanfattningItems(newItems || []);
    setShowSammanfattningModal(true);
  };

  const downloadSammanfattningExcel = async (items) => {
    try {
      const response = await fetchWithAuth('/api/heroma/sammanfattning-excel', {
        method: 'POST',
        body: JSON.stringify(items)
      });
      if (!response.ok) throw new Error('Fel vid nedladdning av Excel');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ImportSammanfattning.xlsx';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.error(error.message || 'Fel vid nedladdning av Excel');
    }
  };

  const uploadPasstider = async (key, userId) => {
    if (!passtiderId) {
      setImportMessages([{ type: 'error', text: 'Ingen fil vald.' }]);
      setSammanfattningItems([]);
      setShowSammanfattningModal(true);
      return;
    }
    setImportMessages([]);
    setSammanfattningItems([]);
    setLoadingArr(prev => ({ ...prev, [key]: true }));
    try {
      let url = '/api/passtider/passtider';
      if (userId) url += `?userId=${userId}`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({ documentId: passtiderId, station: station })
      });
      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg || 'Fel vid uppladdning av Excel-fil.');
      }
      const exists = await checkIfPasstiderExists(station);
      if (exists) {
        const result = await response.json();
        setStationAntalEnheter(result.stationAntal);
        clearReportNames();
        setPasstiderHeromaNotMatching(true);
        setImportMessages([{ type: 'success', text: '✅ Passtider inläst.' }]);
        setShowSammanfattningModal(true);
      } else {
        setImportMessages([{ type: 'warning', text: 'Inga passtider inlästa. Granska filen.' }]);
        setShowSammanfattningModal(true);
      }
    } catch (error) {
      setImportMessages([{ type: 'error', text: error.message || 'Fel vid uppladdning av Excel-fil.' }]);
      setShowSammanfattningModal(true);
    } finally {
      await checkIfPasstiderExists(station);
      setLoadingArr(prev => ({ ...prev, [key]: false }));
    }
  }

  const prepareForNewHeromaAsync = async () => {
    clearReportNames();
  }

  const isMissingDailyData = dailyReportNames.length > 0 && dailyReportNames[0] === 'Underlag saknas';

  if (!isAuthorized) return <></>;

  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      {showSammanfattningModal && (
        <DraggableToolbar initialPosition={{ x: 40, y: 60 }}>
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-border tw-border-blue-300 tw-w-[62rem] tw-z-50">
            {/* Titelfält – drag-handtag */}
            <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-t-2xl tw-flex tw-justify-between tw-items-center tw-cursor-grab">
              <span className="tw-font-bold tw-text-base tw-select-none">
                ☰ Importsammanfattning
              </span>
              <button
                onClick={() => setShowSammanfattningModal(false)}
                className="tw-text-white hover:tw-text-red-200 tw-font-bold tw-text-lg tw-leading-none tw-bg-transparent tw-border-0 tw-ml-4"
                title="Stäng"
              >
                ✕
              </button>
            </div>

            {/* Import-meddelanden */}
            {importMessages.length > 0 && (
              <div className="tw-px-4 tw-pt-3 tw-pb-1 tw-flex tw-flex-col tw-gap-1 tw-overflow-y-auto tw-max-h-[25vh]">
                {importMessages.map((msg, idx) => (
                  <div key={idx} className={`tw-px-3 tw-py-2 tw-rounded tw-text-sm tw-font-semibold ${
                    msg.type === 'success'
                      ? 'tw-bg-green-50 tw-text-green-800 tw-border tw-border-green-200'
                      : msg.type === 'warning'
                        ? 'tw-bg-yellow-50 tw-text-yellow-800 tw-border tw-border-yellow-200'
                        : 'tw-bg-red-50 tw-text-red-800 tw-border tw-border-red-200'
                  }`}>
                    {msg.text}
                  </div>
                ))}
              </div>
            )}

            {/* Besättningsnummer-tabell (visas bara om det finns avvikelser) */}
            {sammanfattningItems.length > 0 && (
              <>
                <div className="tw-px-4 tw-pt-2 tw-pb-1">
                  <p className="tw-text-sm tw-text-gray-500">
                    Besättningsnummer: &nbsp;
                    {sammanfattningItems.filter(i => i.typ === 'Ej i schemat').length} ej i schemat &nbsp;·&nbsp;
                    {sammanfattningItems.filter(i => i.typ === 'Nummer stegat 2->3').length} nummer stegade 2-&gt;3 &nbsp;·&nbsp;
                    {sammanfattningItems.filter(i => i.typ === 'Dubblett').length} dubbletter
                  </p>
                </div>
                {(() => {
                  const handleSort = (key) => {
                    if (sammanfattningSortKey === key) {
                      setSammanfattningSortAsc(a => !a);
                    } else {
                      setSammanfattningSortKey(key);
                      setSammanfattningSortAsc(true);
                    }
                  };
                  const sortIndicator = (key) => {
                    if (sammanfattningSortKey !== key) return ' ⇅';
                    return sammanfattningSortAsc ? ' ↑' : ' ↓';
                  };
                  const sorted = [...sammanfattningItems].sort((a, b) => {
                    const av = (a[sammanfattningSortKey] ?? '').toString();
                    const bv = (b[sammanfattningSortKey] ?? '').toString();
                    return sammanfattningSortAsc ? av.localeCompare(bv, 'sv') : bv.localeCompare(av, 'sv');
                  });
                  const thClass = 'tw-p-2 tw-font-semibold tw-border-b tw-cursor-pointer tw-select-none hover:tw-bg-yellow-300 tw-whitespace-nowrap';
                  return (
                    <div className="tw-overflow-y-auto tw-max-h-[35vh] tw-border-t tw-border-b tw-mx-4">
                      <table className="tw-w-full tw-text-sm tw-text-left tw-border-collapse">
                        <thead className="tw-sticky tw-top-0 tw-bg-yellow-200">
                          <tr>
                            <th className={thClass} onClick={() => handleSort('typ')}>Typ{sortIndicator('typ')}</th>
                            <th className={thClass} onClick={() => handleSort('datum')}>Datum{sortIndicator('datum')}</th>
                            <th className={thClass} onClick={() => handleSort('tidsIntervall')}>Tidsintervall{sortIndicator('tidsIntervall')}</th>
                            <th className={thClass} onClick={() => handleSort('besattningsNummer')}>Besättningsnr{sortIndicator('besattningsNummer')}</th>
                            <th className={thClass} onClick={() => handleSort('namn')}>Namn{sortIndicator('namn')}</th>
                            <th className={thClass} onClick={() => handleSort('tidigareNummer')}>Från nr{sortIndicator('tidigareNummer')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50'}>
                              <td className="tw-p-2 tw-border-b">
                                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-bold ${
                                  item.typ === 'Ej i schemat'
                                    ? 'tw-bg-red-100 tw-text-red-700'
                                    : item.typ === 'Dubblett'
                                      ? 'tw-bg-green-100 tw-text-green-700'
                                      : 'tw-bg-blue-100 tw-text-blue-700'
                                }`}>
                                  {item.typ}
                                </span>
                              </td>
                              <td className="tw-p-2 tw-border-b tw-whitespace-nowrap">{item.datum}</td>
                              <td className="tw-p-2 tw-border-b tw-font-mono tw-whitespace-nowrap">{item.tidsIntervall || '—'}</td>
                              <td className="tw-p-2 tw-border-b tw-font-mono tw-font-semibold">{item.besattningsNummer}</td>
                              <td className="tw-p-2 tw-border-b">{item.namn}</td>
                              <td className="tw-p-2 tw-border-b tw-font-mono tw-text-gray-500">{item.tidigareNummer ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </>
            )}

            <div className="tw-flex tw-justify-between tw-px-4 tw-py-3">
              {sammanfattningItems.length > 0 ? (
                <button
                  onClick={() => downloadSammanfattningExcel(sammanfattningItems)}
                  className="tw-bg-green-600 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-green-700 tw-font-semibold tw-text-sm"
                >
                  ⬇ Ladda ner Excel
                </button>
              ) : (
                <span />
              )}
              <button
                onClick={() => setShowSammanfattningModal(false)}
                className="tw-bg-gray-200 tw-text-gray-800 tw-px-4 tw-py-2 tw-rounded hover:tw-bg-gray-300 tw-font-semibold tw-text-sm"
              >
                Stäng
              </button>
            </div>
          </div>
        </DraggableToolbar>
      )}

      <div className="tw-max-w-6xl tw-mx-auto tw-py-3">
        <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-w-[1700px] tw-max-w-full">
          <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
            <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Arbetsscheman för Ambulanspersonal</h3>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4 tw-pl-10">
            <div className="tw-flex tw-flex-col tw-space-y-2">
              <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-min-h-[40px]">
                  {anyLoading() && <BattenburgLoader />}
                </div>
                <div className="tw-min-h-[40px]">
                  {impersonatedUserId && impersonatedUser && (
                    <div className="tw-mb-2">
                      <h3 className="tw-font-semibold tw-text-lg tw-text-gray-800 tw-text-center">
                        Du agerar som användare:
                        <span className="tw-text-red-500 hover:tw-text-red-700 tw-ml-1">
                          <strong>{impersonatedUser.userName}</strong>
                        </span>
                      </h3>
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-pb-5 tw-min-h-[40px]">
                <label htmlFor="stationer-select" className="tw-text-sm tw-font-bold tw-mr-2">
                  Station:
                </label>
                {stationer.length > 0 && (
                  <select
                    id="stationer-select"
                    value={station}
                    disabled={!isAuthorized || anyLoading()}
                    onChange={(e) => setStation(e.target.value)}
                    className="tw-min-w-[160px] tw-border tw-p-2 tw-mr-2 tw-text-sm tw-leading-normal tw-font-bold"
                  >
                    {station == '---' && <option key='placeholder_station' value="">Välj station</option>}
                    {stationer.map((table) => (
                      <option key={table} value={table}>{table}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="tw-flex tw-items-start tw-space-x-2">
                <div className="tw-flex tw-flex-col tw-pb-5">
                  <select
                    value={passtiderId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setPasstiderId(selectedId);
                      setHasPasstider(false);
                      setSelectedPasstiderFile(documents.passtider.find(f => f.id === parseInt(selectedId))?.name);
                    }}
                    disabled={!isAuthorized || anyLoading()}
                    className="tw-min-w-[433px] tw-border tw-p-2 tw-mr-2 tw-text-sm tw-leading-normal tw-font-bold"
                  >
                    {!isAuthorized ? (
                      <option key='placeholder_2' value="">Inloggning krävs</option>
                    ) : (
                      <>
                        <option key='placeholder_3' value="">Välj fil</option>
                        {documents.passtider.map((file) => (
                          <option key={file.id} value={file.id}>{file.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                  <p className="tw-font-bold tw-text-red-600 tw-mt-1 tw-h-5 tw-text-sm">
                    {passtiderHeromaNotMatching ? 'Passtider inläst, läs in ny Heroma?' : ''}
                  </p>
                </div>

                <div className="tw-flex tw-flex-col">
                  <button
                    onClick={async () => {
                      uploadPasstider('btn4', impersonatedUserId);
                    }}
                    disabled={anyLoading() || !passtiderId || !isAuthorized}
                    className={
                      `tw-min-w-[450px] tw-mr-3 tw-py-2 tw-px-4 tw-rounded tw-text-sm tw-leading-normal tw-font-bold ` +
                      (loadingArr['btn4']
                        ? 'tw-bg-yellow-300 tw-text-black tw-shadow-[0_0_6px_#fde047]'
                        : !hasPasstider || !passtiderId
                          ? `tw-bg-red-500 tw-text-white`
                          : `tw-bg-lime-500 tw-text-white ${(!anyLoading() && hasPasstider) ? 'hover:tw-bg-lime-700' : ''} tw-shadow-[0_0_6px_#84cc16]`)
                    }
                  >
                    {loadingArr['btn4'] ? 'Läser in Passtider...' : 'Läs in Passtider'}
                  </button>
                  <p className="tw-font-bold">
                    Senaste: {hasPasstider && passtiderId ? selectedPasstiderFile : '---'}
                  </p>
                </div>
                {stationAntalEnheter && (
                  <div className="tw-overflow-y-auto tw-text-sm tw-font-medium tw-mb-3 tw-pl-5 tw-w-[350px] tw-font-bold tw-whitespace-pre-line">
                    {stationAntalEnheter}
                  </div>
                )}
              </div>
              <br />
              <div className="tw-flex tw-items-start tw-space-x-2">
                <div className="tw-flex tw-flex-col tw-pb-5">
                  <select
                    value={heromaId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setHeromaId(selectedId);
                      setHasHeroma(false);
                      setStationAntalPersoner('');
                    }}
                    disabled={!isAuthorized || anyLoading()}
                    className="tw-min-w-[433px] tw-border tw-p-2 tw-mr-2 tw-text-sm tw-leading-normal tw-font-bold"
                  >
                    {!isAuthorized ? (
                      <option key='placeholder_8' value="">Inloggning krävs</option>
                    ) : (
                      <>
                        <option key='placeholder_4' value="">Välj fil</option>
                        {documents.heroma.map((file) => (
                          <option key={file.id} value={file.id}>{file.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                <div className="tw-flex tw-flex-col">
                  <button
                    onClick={async () => {
                      await prepareForNewHeromaAsync();
                      await uploadHeromaSchema('btn3', impersonatedUserId);
                    }}
                    disabled={anyLoading() || !hasPasstider || !heromaId || !isAuthorized}
                    className={
                      `tw-min-w-[450px] tw-mr-3 tw-py-2 tw-px-4 tw-rounded tw-text-sm tw-leading-normal tw-font-bold ` +
                      (loadingArr['btn3']
                        ? 'tw-bg-yellow-300 tw-text-black tw-shadow-[0_0_6px_#fde047]'
                        : !hasHeroma || !heromaId 
                          ? `tw-bg-red-500 tw-text-white`
                          : `tw-bg-lime-500 tw-text-white ${!anyLoading() ? 'hover:tw-bg-lime-700' : ''} tw-shadow-[0_0_6px_#84cc16]`)
                    }
                  >
                    {loadingArr['btn3'] ? 'Läser in Schemadata (Heroma)...' : 'Läs in Schemadata (Heroma)'}
                  </button>
                  <p className="tw-font-bold tw-whitespace-pre-line">
                    Senaste: {hasHeroma && heromaId ? selectedHeromaFile : '---'}
                  </p>
                </div>

                {stationAntalPersoner && (
                  <div className="tw-overflow-y-auto tw-text-sm tw-font-medium tw-mb-3 tw-pl-5 tw-w-[350px] tw-font-bold tw-whitespace-pre-line">
                    {stationAntalPersoner}
                  </div>
                )}
              </div>

              <br />

              <div className="tw-flex tw-items-start tw-space-x-2 tw-pl-36">
                <div className="tw-flex tw-flex-col tw-pb-5">
                  <select
                    id="day-select"
                    value={selectedDay}
                    disabled={isMissingDailyData || !isAuthorized || anyLoading()}
                    onChange={(e) => { setSelectedDay(e.target.value); setHtmlReportDone(false); }}
                    className="tw-min-w-[270px] tw-border tw-p-2 tw-mr-2 tw-text-sm tw-leading-normal tw-font-bold"
                  >
                    {isAuthorized && !isMissingDailyData && <option key='placeholder_5' value="">Välj dag</option>}
                    {dailyReportNames.map((table, idx) => (
                      <option key={idx} value={table}>{table}</option>
                    ))}
                  </select>
                </div>

                <div className="tw-flex tw-flex-col">
                  <button
                    onClick={async () => {
                      setHtmlReportDone(false);
                      try {
                        await getAktuellaPersoner();
                        await downloaderRef.current?.createHtmlReport('btn1', impersonatedUserId);
                        setHtmlReportDone(true);
                      } catch (error) {
                        console.error("Fel vid HTML-rapport:", error);
                      }
                    }}
                    disabled={!isAuthorized || !selectedDay || !hasPasstider || !hasHeroma || anyLoading()}
                    className={
                      `tw-min-w-[450px] tw-mr-3 tw-py-2 tw-px-4 tw-rounded tw-text-sm tw-leading-normal tw-font-bold ` +
                      (loadingArr['btn1']
                        ? 'tw-bg-yellow-300 tw-text-black tw-shadow-[0_0_6px_#fde047]'
                        : !htmlReportDone || !selectedDay
                          ? `tw-bg-yellow-300 tw-text-blue tw-shadow-[0_0_6px_#fde047]`
                          : `tw-bg-lime-500 tw-text-white ${!anyLoading() ? 'hover:tw-bg-lime-700' : ''} tw-shadow-[0_0_6px_#84cc16]`)
                    }
                  >
                    {loadingArr['btn1']
                      ? `Skapar schema för ${selectedDay}...`
                      : 'Skapa schema i HTML/Excel'}
                  </button>
                  <p className="tw-font-bold">Senaste: {htmlReportDone && selectedDay ? selectedDay : '---'}</p>
                </div>
              </div>

            </div>

            <ReportDownloader ref={downloaderRef}
              selectedDay={selectedDay}
              selectedMonth={selectedMonth}
              selectedMonthVacancy={selectedMonthVacancy}
              setLoadingArr={setLoadingArr}
              setMonthReportDone={setMonthReportDone}
              station={station}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
